import React, { useCallback } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import {
    WAVEFORM_BAR_GAP_PX,
    WAVEFORM_BAR_WIDTH_PX,
    getWaveformTrackWidth,
} from 'src/views/OmniChannel/utils/audioRecorder';

const useStyles = makeStyles(() => ({
    root: {
        display: 'flex',
        alignItems: 'center',
        flex: 1,
        minWidth: 0,
        height: 32,
    },
    track: {
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        gap: WAVEFORM_BAR_GAP_PX,
        height: '100%',
        flexShrink: 0,
    },
    bar: {
        width: WAVEFORM_BAR_WIDTH_PX,
        borderRadius: 999,
        transition: 'height 0.08s ease, background-color 0.08s linear',
        flexShrink: 0,
    },
    scrubber: {
        position: 'absolute',
        top: '50%',
        width: 10,
        height: 10,
        borderRadius: '50%',
        transform: 'translate(-50%, -50%)',
        boxShadow: '0 0 0 2px rgba(255, 255, 255, 0.85)',
        pointerEvents: 'none',
        zIndex: 1,
    },
}));

type TAudioWaveformProps = {
    levels: number[];
    progress?: number;
    activeColor?: string;
    inactiveColor?: string;
    scrubberColor?: string;
    showScrubber?: boolean;
    onSeek?: (progress: number) => void;
};

const getBarCenterProgress = (index: number, total: number) =>
    ((index + 0.5) / total) * 100;

export const AudioWaveform = ({
    levels,
    progress = 0,
    activeColor = '#ffffff',
    inactiveColor = 'rgba(255, 255, 255, 0.45)',
    scrubberColor,
    showScrubber = false,
    onSeek,
}: TAudioWaveformProps) => {
    const classes = useStyles();
    const clampedProgress = Math.min(100, Math.max(0, progress));
    const barCount = Math.max(levels.length, 1);
    const trackWidth = getWaveformTrackWidth(barCount);

    const handleSeek = useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            if (!onSeek) return;

            const rect = event.currentTarget.getBoundingClientRect();
            const ratio = (event.clientX - rect.left) / rect.width;

            onSeek(Math.min(1, Math.max(0, ratio)));
        },
        [onSeek]
    );

    return (
        <Box className={classes.root}>
            <Box
                className={classes.track}
                style={{ width: trackWidth }}
                onClick={onSeek ? handleSeek : undefined}
                role={onSeek ? 'slider' : undefined}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={clampedProgress}
            >
                {levels.map((level, index) => {
                    const barCenter = getBarCenterProgress(index, barCount);
                    const isActive = barCenter <= clampedProgress;

                    return (
                        <Box
                            key={index}
                            className={classes.bar}
                            style={{
                                height: `${Math.max(4, level * 28)}px`,
                                backgroundColor: isActive
                                    ? activeColor
                                    : inactiveColor,
                            }}
                        />
                    );
                })}

                {showScrubber && (
                    <Box
                        className={classes.scrubber}
                        style={{
                            left: `${clampedProgress}%`,
                            backgroundColor: scrubberColor || activeColor,
                        }}
                    />
                )}
            </Box>
        </Box>
    );
};
